import { Box, Icon, useBoolean } from '@chakra-ui/react';
import {
  FC,
  forwardRef,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GridLayout, { ReactGridLayoutProps } from 'react-grid-layout';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import './style.css';
import { generateBackground } from './utils';
import { CenterSpinner } from '../ats';
import { Dragsvg } from '@kula-ai/icons-core';
import { useKFlags } from '@/hooks/use-k-flags';
import DotsCorner from '@/icons/DotsCorner';

interface GridConfig {
  x: number;
  y: number;
  w: number;
  h: number;
  minH?: number;
  minW?: number;
  maxH?: number;
  maxW?: number;

  key: string;
  render: (props: { isHovering: boolean }) => ReactElement;
}

interface ResizableGridProps {
  columns?: number;
  rowHeight?: number;
  config: GridConfig[];
  reactGridLayoutProps?: ReactGridLayoutProps;
  itemProps?: any;
  showGridAlways?: boolean;
  isPrintView?: boolean;

  onLayoutChange?: (config: Omit<GridConfig, 'render'>[]) => void;
}

export const Item: FC<{
  isDragging: boolean;
  children?: any;
  render: (props: { isHovering: boolean }) => ReactElement;
  isPrintView?: boolean;
  itemProps?: any;
}> = forwardRef(
  ({ isDragging, children, render, itemProps, isPrintView, ...props }, ref) => {
    const { showDragHandle = true, showBoxShadow = true } = itemProps || {};

    const { feReportsV3 } = useKFlags();
    const [isHovering, setIsHovering] = useState(false);

    return (
      <Box
        borderRadius="6px"
        padding="8px"
        backgroundColor="white"
        border={'1px solid'}
        borderColor="border-color"
        position="relative"
        _hover={{
          boxShadow: showBoxShadow && '0px 4px 8px rgba(0, 0, 0, 0.1)',
        }}
        ref={ref as any}
        onMouseEnter={() => (feReportsV3 ? undefined : setIsHovering(true))}
        onMouseLeave={() => (feReportsV3 ? undefined : setIsHovering(false))}
        {...props}
        height={isPrintView ? '100%' : 'auto'}
        className={isPrintView ? 'report-tile' : undefined}
      >
        {showDragHandle ? (
          <Box
            className="drag-handle"
            cursor={isDragging ? 'grabbing' : 'grab'}
            position="absolute"
            mt={'6px'}
          >
            <Dragsvg />
          </Box>
        ) : (
          ''
        )}
        <Box
          width={showDragHandle ? 'calc(100% - 36px)' : '100%'}
          height="100%"
          marginLeft={showDragHandle && '18px'}
          paddingX="8px"
        >
          {render({ isHovering })}
        </Box>
        {children}
      </Box>
    );
  }
);

Item.displayName = 'GridItem';

export const ResizableGrid: FC<ResizableGridProps> = ({
  columns = 12,
  rowHeight = 280,
  config = [],
  onLayoutChange,
  reactGridLayoutProps,
  itemProps,
  showGridAlways = false,
  isPrintView = false,
}) => {
  // @ts-ignore
  const containerRef = useRef<HTMLDivElement>();
  const [gridWidth, setGridWidth] = useState(containerRef.current?.clientWidth);
  const [gridHeight, setGridHeight] = useState(
    containerRef.current?.clientHeight
  );

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      setGridWidth(containerRef.current?.clientWidth);
      setGridHeight(containerRef.current?.clientHeight);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isEditing, { on: startEditing, off: stopEditing }] = useBoolean(false);
  const [dragElement, setDragElement] = useState<any>(false);

  const layout = useMemo(() => {
    return config.map(({ render, key, ...rest }) => ({ i: key, ...rest }));
  }, [config]);

  useEffect(() => {
    if (containerRef.current) {
      // Set initial width
      setGridWidth(containerRef.current.clientWidth);
      setIsInitialized(true);
    }

    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0].contentRect.width;
      setGridWidth(newWidth);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Don't render grid until we have a width
  if (!isInitialized || gridWidth === 0) {
    return (
      <Box ref={containerRef} minHeight="500px">
        <CenterSpinner h="400px" />
      </Box>
    );
  }

  // For print view, create a custom CSS Grid layout that respects positioning used for printing
  if (isPrintView) {
    return (
      <Box ref={containerRef} width="100%">
        <Box
          display="grid"
          gridTemplateColumns={`repeat(${columns}, 1fr)`}
          gridAutoRows="minmax(10px, auto)"
          gap="12px"
          width="100%"
          className="custom-print-view-grid"
        >
          {config.map(({ render, key, x, y, w, h }) => {
            return (
              <Box
                key={key}
                gridColumn={`${x + 1} / span ${w}`}
                gridRow={`${y + 1} / span ${Math.max(h, 1)}`}
                minHeight="auto"
                className="grid-item report-tile"
              >
                <Item
                  isDragging={false}
                  render={render}
                  isPrintView={isPrintView}
                  itemProps={itemProps}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} h="100%">
      {/* @ts-ignore */}
      <GridLayout
        cols={columns}
        rowHeight={rowHeight}
        width={gridWidth - 5}
        margin={[12, 12]}
        containerPadding={[0, 0]}
        useCSSTransforms
        transformScale={1}
        layout={layout}
        onDragStart={(_, _before, after) => {
          setDragElement(after.i);
          startEditing();
        }}
        onDragStop={(layout) => {
          onLayoutChange?.(
            layout.map(({ h, w, x, y, i, minH, minW, maxH, maxW }) => ({
              x,
              y,
              h,
              w,
              key: i,
              minH,
              minW,
              maxH,
              maxW,
            }))
          );
          setDragElement(false);
          setTimeout(() => {
            stopEditing();
          }, 500);
        }}
        onResizeStart={() => {
          startEditing();
        }}
        onResizeStop={(layout) => {
          onLayoutChange?.(
            layout.map(({ h, w, x, y, i, minH, minW, maxH, maxW }) => ({
              x,
              y,
              h,
              w,
              key: i,
              minH,
              minW,
              maxH,
              maxW,
            }))
          );
          setTimeout(() => {
            stopEditing();
          }, 500);
        }}
        style={{
          backgroundImage: generateBackground({
            gridWidth,
            gridHeight,
            rowHeight,
            columns,
            margin: 12,
            isEditing: isEditing || showGridAlways,
          }),
          overflow: 'hidden',
          backgroundPosition: '0px 0px',
          backgroundSize: '100%',
          minHeight: isPrintView ? 'auto' : '100%',
          ...(isPrintView && { height: 'auto' }),
        }}
        draggableHandle=".drag-handle"
        // todo: playground with the below props for floating items
        // isBounded
        // compactType={null}
        // preventCollision={false}
        // verticalCompact={false}
        resizeHandle={
          reactGridLayoutProps?.isResizable ? (
            <Box position="absolute" bottom="-3px" right="-3px">
              <Icon cursor="se-resize" as={DotsCorner} w="30px" h="30px" />
            </Box>
          ) : (
            <></>
          )
        }
        {...reactGridLayoutProps}
      >
        {config.map(({ render, key }) => {
          return (
            <Item
              isDragging={dragElement === key}
              key={key}
              render={render}
              itemProps={itemProps}
            />
          );
        })}
      </GridLayout>
    </Box>
  );
};
